import { inject } from 'vue';
import * as Sentry from '@sentry/vue';

export const SENTRY_KEY = 'sentry';

let sentry = null;

const SEVERITY = {
    FATAL: 'fatal',
    ERROR: 'error',
    WARNING: 'warning',
    INFO: 'info',
    DEBUG: 'debug',
};

const noop = () => {};

function setUser({ casId }) {
    try {
        if (casId) {
            Sentry.getCurrentScope().setUser({
                id: casId,
            });
        }
    // eslint-disable-next-line no-unused-vars
    } catch (e) {
        // swallow sentry failures
    }
}

function log(message, data = {}, level = SEVERITY.WARNING) {
    try {
        Sentry.withScope((scope) => {
            scope.setLevel(level);

            Object.keys(data).forEach((key) => {
                scope.setExtra(key, data[key]);
            });

            Sentry.captureMessage(message, { level });
        });
    // eslint-disable-next-line no-unused-vars
    } catch (e) {
        // swallow sentry failures
    }
}

function captureException(error, captureOptions = {}) {
    try {
        Sentry.withScope((scope) => {
            const { message, data } = captureOptions;

            if (message) {
                scope.setTransactionName(message);
            }

            if (data) {
                Object.keys(data).forEach((key) => {
                    scope.setExtra(key, data[key]);
                });
            }

            Sentry.captureException(error);
        });
    // eslint-disable-next-line no-unused-vars
    } catch (e) {
        // swallow sentry failures
    }
}

export const createSentry = (options) => {
    const {
        dsn,
        org,
        router,
        release,
        enabled,
        ignoreErrors = [],
        integrations = [],
        denyUrls = [],
    } = options;

    sentry = {
        install(app) {
            if (enabled) {
                Sentry.init({
                    app,
                    dsn,
                    org,
                    release,
                    ignoreErrors: [
                        /^Request failed with status code \d{3}$/,
                        /Object Not Found Matching Id:\d+/,
                        'Network error: Failed to fetch',
                        '$t is not a function',
                        'ChunkLoadError',
                        'JWT is expired',
                        'JWT is not present in the auth session',
                        'Redirected when going from', // https://github.com/vuejs/vue-router/blob/v3.3.4/src/history/errors.js#L13
                        'Avoided redundant navigation', // https://github.com/vuejs/vue-router/blob/v3.3.4/src/history/errors.js#L24
                        'Navigation cancelled', // https://github.com/vuejs/vue-router/blob/v3.3.4/src/history/errors.js#L33
                        'Navigation aborted', // https://github.com/vuejs/vue-router/blob/v3.3.4/src/history/errors.js#L44
                        ...ignoreErrors,
                    ],
                    integrations: [
                        Sentry.browserTracingIntegration({ router }),
                        ...integrations,
                    ],
                    denyUrls: [
                        /cdn\.pendo\.io/i,
                        ...denyUrls,
                    ],
                });
            }

            app.provide(SENTRY_KEY, sentry);
        },
        setUser: enabled ? setUser : noop,
        log: enabled ? log : noop,
        captureException: enabled ? captureException : noop,
        severity: SEVERITY,
    };

    return sentry;
};

export const useSentry = () => {
    return inject(SENTRY_KEY);
};

// Specifically being exported for Vuex actions, where we can't use the composition API. This is a workaround until we switch to Pinia.
export const getSentry = () => {
    return sentry || {
        log: noop,
        captureException: noop,
        setUser: noop,
        severity: SEVERITY,
    };
};
